import { compact } from 'lodash';

import {
  AIOnboardingModalFormShape,
  AIOnboardingModalFormShapeKeys,
} from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.types';
import { mapAssetsToFormData } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/assets.fields.utils';
import {
  getMissingRequiredEntityFields,
  mapEntitiesToFormData,
  mapEntitiesToInput,
} from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/entities.fields.utils';
import { getBulkCreateHoldingsInput } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/holdings.utils';
import {
  mapIndividualsToFormData,
  mapIndividualsToInput,
} from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/individuals.fields.utils';
import { mapLiabilitiesToFormData } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/liabilities.fields.utils';
import { mapLifeInsurancePoliciesToFormData } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/lifeInsurancePolicies.fields.utils';
import {
  mapOrganizationsToFormData,
  mapOrganizationsToInput,
} from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/organizations.fields.utils';
import {
  getMissingRequiredTestamentaryEntityFields,
  mapTestamentaryEntitiesToFormData,
  mapTestamentaryEntitiesToInput,
} from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/testamentaryEntities.fields.utils';
import {
  AiOnboardingModal_HouseholdFragment,
  AiOnboardingModalBulkCreateMutationVariables,
} from '@/modules/aiOnboarding/AIOnboardingModal/graphql/aiOnboardingModal.generated';
import { AiSuggestionForReviewCard } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import type { KGV2NodeForGrouping } from '@/modules/aiSuggestions/kgV2Resolution.utils';
import type {
  AugmentedCreateLiabilityInput,
  UpsertAssetToCurrentValuationInput,
} from '@/types/schema';
import { AiSuggestionKind } from '@/types/schema';

export function getFormDataForSuggestion(
  formData: AIOnboardingModalFormShape,
  suggestionID: string
): AIOnboardingModalFormShape {
  const isSelectedSuggestion = (row: { _suggestionID?: string }) =>
    row._suggestionID === suggestionID;

  return {
    individuals: formData.individuals.filter(isSelectedSuggestion),
    organizations: formData.organizations.filter(isSelectedSuggestion),
    entities: formData.entities.filter(isSelectedSuggestion),
    testamentaryEntities:
      formData.testamentaryEntities.filter(isSelectedSuggestion),
    assets: formData.assets.filter(isSelectedSuggestion),
    liabilities: formData.liabilities.filter(isSelectedSuggestion),
    lifeInsurancePolicies:
      formData.lifeInsurancePolicies.filter(isSelectedSuggestion),
  };
}

///////////////////////////////////////////////////////////
// SUGGESTION DATA -> FORM DATA
///////////////////////////////////////////////////////////

interface DataToFormOpts {
  /**
   * KG V2 nodes keyed by ID, used to resolve entity principals directly from
   * `principalNodeIDs`.
   */
  principalNodesById?: Map<string, KGV2NodeForGrouping>;
}

/**
 * Converts the onboarding suggestions data to the form data.
 *
 * @param suggestions
 * @param household
 * @param opts
 */
export function dataToForm(
  suggestions: AiSuggestionForReviewCard[],
  household: AiOnboardingModal_HouseholdFragment,
  opts: DataToFormOpts = {}
): AIOnboardingModalFormShape {
  const { principalNodesById = new Map() } = opts;

  return {
    individuals: mapIndividualsToFormData(
      suggestions.filter(
        (a) => a.kind === AiSuggestionKind.OnboardingIndividual
      ),
      household
    ),
    organizations: mapOrganizationsToFormData(
      suggestions.filter(
        (a) => a.kind === AiSuggestionKind.OnboardingOrganization
      )
    ),
    entities: mapEntitiesToFormData(
      suggestions.filter((a) => a.kind === AiSuggestionKind.OnboardingEntity),
      principalNodesById
    ),
    testamentaryEntities: mapTestamentaryEntitiesToFormData(
      suggestions.filter(
        (a) => a.kind === AiSuggestionKind.OnboardingTestamentaryEntity
      ),
      household
    ),
    assets: mapAssetsToFormData(
      suggestions.filter((a) => a.kind === AiSuggestionKind.Asset),
      household
    ),
    liabilities: mapLiabilitiesToFormData(
      suggestions.filter((a) => a.kind === AiSuggestionKind.Liability),
      household
    ),
    lifeInsurancePolicies: mapLifeInsurancePoliciesToFormData(
      suggestions.filter((a) => a.kind === AiSuggestionKind.LifeInsurance),
      household
    ),
  };
}

interface AppendSuggestionsForSubmissionOpts extends DataToFormOpts {
  /** The form paths the current modal variant submits. */
  formPaths: AIOnboardingModalFormShapeKeys[];
  /** Suggestion IDs the user already reviewed in the modal; never re-added. */
  excludedSuggestionIDs: string[];
  household: AiOnboardingModal_HouseholdFragment;
}

/**
 * Appends default-valued rows for every pending suggestion in the modal's
 * submission scope that is not already represented in the (page-local) form.
 *
 * The paginated Review-all form only holds one page of suggestions, but
 * Confirm/Confirm all must submit the whole scope: suggestions on pages the
 * user never visited are created with their suggested values, exactly like
 * the review card's "Create all".
 *
 * @param formData - The current page's form data (with any user edits)
 * @param suggestionsForSubmission - Every pending suggestion in the modal's scope
 * @param opts
 */
export function appendSuggestionsForSubmissionToFormData(
  formData: AIOnboardingModalFormShape,
  suggestionsForSubmission: AiSuggestionForReviewCard[],
  opts: AppendSuggestionsForSubmissionOpts
): AIOnboardingModalFormShape {
  const { formPaths, excludedSuggestionIDs, household, ...dataToFormOpts } =
    opts;

  const formSuggestionIDs = new Set(
    compact(
      formPaths.flatMap((path) =>
        formData[path].map((row) => row._suggestionID)
      )
    )
  );

  const missingSuggestions = suggestionsForSubmission.filter(
    (suggestion) =>
      !suggestion.acceptanceStatus &&
      !formSuggestionIDs.has(suggestion.id) &&
      !excludedSuggestionIDs.includes(suggestion.id)
  );

  if (missingSuggestions.length === 0) {
    return formData;
  }

  const defaultRows = dataToForm(missingSuggestions, household, dataToFormOpts);

  return {
    individuals: formPaths.includes('individuals')
      ? [...formData.individuals, ...defaultRows.individuals]
      : formData.individuals,
    organizations: formPaths.includes('organizations')
      ? [...formData.organizations, ...defaultRows.organizations]
      : formData.organizations,
    entities: formPaths.includes('entities')
      ? [...formData.entities, ...defaultRows.entities]
      : formData.entities,
    testamentaryEntities: formPaths.includes('testamentaryEntities')
      ? [...formData.testamentaryEntities, ...defaultRows.testamentaryEntities]
      : formData.testamentaryEntities,
    assets: formPaths.includes('assets')
      ? [...formData.assets, ...defaultRows.assets]
      : formData.assets,
    liabilities: formPaths.includes('liabilities')
      ? [...formData.liabilities, ...defaultRows.liabilities]
      : formData.liabilities,
    lifeInsurancePolicies: formPaths.includes('lifeInsurancePolicies')
      ? [
          ...formData.lifeInsurancePolicies,
          ...defaultRows.lifeInsurancePolicies,
        ]
      : formData.lifeInsurancePolicies,
  };
}

///////////////////////////////////////////////////////////
// FORM DATA -> MUTATION INPUT
///////////////////////////////////////////////////////////

/**
 * Converts the form data to the input data for the mutation. Only includes the
 * form paths that are included in the formPaths array.
 *
 * @param formData
 * @param formPaths
 * @param household
 * @param suggestionsByID
 */
export function formDataToInput(
  formData: AIOnboardingModalFormShape,
  formPaths: AIOnboardingModalFormShapeKeys[],
  household: AiOnboardingModal_HouseholdFragment,
  suggestionsByID: Record<string, AiSuggestionForReviewCard>
): Omit<
  AiOnboardingModalBulkCreateMutationVariables,
  'acknowledgeSuggestionsInput'
> & {
  createLiabilitiesInput: AugmentedCreateLiabilityInput[];
  upsertAssetsInput: UpsertAssetToCurrentValuationInput[];
  acceptedSuggestionIDs: string[];
} {
  const cleanedFormData = cleanFormData(formData);

  const {
    individuals,
    organizations,
    entities,
    testamentaryEntities,
    assets,
    liabilities,
    lifeInsurancePolicies,
  } = cleanedFormData;

  const {
    updateEntitiesWithPoliciesInput,
    createLiabilitiesInput,
    upsertAssetsInput,
  } = getBulkCreateHoldingsInput(
    assets,
    liabilities,
    lifeInsurancePolicies,
    formPaths,
    household,
    suggestionsByID
  );

  return {
    individualsInput: formPaths.includes('individuals')
      ? mapIndividualsToInput(individuals, household, suggestionsByID)
      : [],
    organizationsInput: formPaths.includes('organizations')
      ? mapOrganizationsToInput(organizations, household, suggestionsByID)
      : [],
    entitiesInput: formPaths.includes('entities')
      ? mapEntitiesToInput(entities, household, suggestionsByID)
      : [],
    testamentaryEntitiesInput: formPaths.includes('testamentaryEntities')
      ? mapTestamentaryEntitiesToInput(
          testamentaryEntities,
          household,
          suggestionsByID
        )
      : [],
    updateEntitiesWithPoliciesInput,
    upsertAssetsInput,
    createLiabilitiesInput,
    acceptedSuggestionIDs: compact(
      formPaths.flatMap((path) =>
        cleanedFormData[path].map((f) => f._suggestionID)
      )
    ),
  };
}

/**
 * Cleans the form data by removing individuals/organizations/entities/TE's/assets/liabilities/lifeInsurancePolicies
 * that do not have the required fields filled out.
 *
 * individuals must have firstName and lastName,
 * organizations must have organizationName and organizationKind,
 * entities must have an entityName, entityType, and at least one principalId,
 * testamentaryEntities must have testamentaryEntityName,
 * testamentaryEntityKind, and testamentaryEntityAssetLocation,
 * assets must have assetName, assetCategoryId, and ownerId,
 * liabilities must have liabilityName, outstandingBalance, lenderId, and debtorId,
 * lifeInsurancePolicies must have policyType, ownerId, and deathBenefitAmount.
 *
 * This is necessary when creating the individuals/organizations/entities/TE's/assets/liabilities/lifeInsurancePolicies
 * from the AISuggestionReviewCard, since the AIOnboardingModalForm will not
 * allow submission.
 *
 * @param formData
 */
function cleanFormData(
  formData: AIOnboardingModalFormShape
): AIOnboardingModalFormShape {
  return {
    ...formData,
    individuals: formData.individuals.filter((i) => i.firstName && i.lastName),
    organizations: formData.organizations.filter(
      (o) => o.organizationName && o.organizationKind
    ),
    entities: formData.entities.filter(
      (entity) => getMissingRequiredEntityFields(entity).length === 0
    ),
    testamentaryEntities: formData.testamentaryEntities.filter(
      (testamentaryEntity) =>
        getMissingRequiredTestamentaryEntityFields(testamentaryEntity)
          .length === 0
    ),
    assets: formData.assets.filter(
      (a) => a.assetName && a.assetCategoryId && a.ownerId && a.valuation
    ),
    liabilities: formData.liabilities.filter(
      (l) => l.name && l.outstandingBalance && (l.issuerId || l.recipientId)
    ),
    lifeInsurancePolicies: formData.lifeInsurancePolicies.filter(
      (p) => p.policyType && p.ownerId && p.deathBenefitAmount
    ),
  };
}
